/**
 * BBRU Orientation & Resize Fix V2 - BUG-009 Enhanced
 * Auto-closes mobile menu on:
 * - Device orientation change (portrait ↔ landscape)
 * - Browser window resize (crossing mobile/desktop breakpoint)
 * - Viewport width crossing 1024px threshold
 * 
 * Prevents menu from blocking content after rotation/resize
 * Date: 2026-05-24 (V13)
 */

(function() {
    'use strict';
    
    // Configuration
    const MOBILE_BREAKPOINT = 1024; // px - menu should close when crossing this
    const RESIZE_DEBOUNCE = 150; // ms - reduced from 250ms for faster response
    const FORCE_CLOSE_DELAY = 50; // ms - additional forced close after initial close
    
    // State tracking
    let lastOrientation = window.orientation || (window.innerWidth > window.innerHeight ? 90 : 0);
    let lastViewportWidth = window.innerWidth;
    let wasMobile = window.innerWidth < MOBILE_BREAKPOINT;
    let resizeTimer = null;
    let forceCloseTimer = null;
    
    /**
     * Aggressively close mobile menu - targets all possible menu elements
     */
    function closeMobileMenu(reason = 'unknown') {
        let menuWasClosed = false;
        
        // 1. PRIMARY TARGET: Uncheck the main menu checkbox
        const mainMenuCheckbox = document.getElementById('open-mobile-nav');
        if (mainMenuCheckbox) {
            if (mainMenuCheckbox.checked) {
                mainMenuCheckbox.checked = false;
                menuWasClosed = true;
            }
        }
        
        // 2. Target any other menu checkboxes (fallback)
        const allMenuCheckboxes = document.querySelectorAll('input[type="checkbox"][id*="menu"], input[type="checkbox"][id*="nav"]');
        allMenuCheckboxes.forEach(function(checkbox) {
            if (checkbox.checked) {
                checkbox.checked = false;
                menuWasClosed = true;
            }
        });
        
        // 3. Remove active classes from navigation elements
        const mobileNavigation = document.querySelector('.mobile__navigation, nav.mobile__navigation');
        if (mobileNavigation && mobileNavigation.classList.contains('active')) {
            mobileNavigation.classList.remove('active');
        }
        
        // 4. Remove overlay visibility
        const overlays = document.querySelectorAll('.overlay--nav, .overlay.overlay--nav, label[for="open-mobile-nav"].overlay');
        overlays.forEach(function(overlay) {
            overlay.classList.remove('active', 'visible', 'open');
            // Force hide with inline style (aggressive)
            overlay.style.display = 'none';
            // Reset after short delay to allow CSS transitions to work normally
            setTimeout(function() {
                overlay.style.display = '';
            }, 300);
        });
        
        // 5. Remove body scroll locks
        if (document.body.classList.contains('menu-open') || document.body.classList.contains('no-scroll')) {
            document.body.classList.remove('menu-open', 'no-scroll');
        }
        document.body.style.overflow = '';
        
        // 6. Close sidebar menu (if exists)
        const sidebarMenu = document.querySelector('.sidebar-menu');
        if (sidebarMenu && sidebarMenu.classList.contains('active')) {
            sidebarMenu.classList.remove('active');
        }
        
        // 7. Force-close any other active menu states
        const activeMenus = document.querySelectorAll('[class*="mobile"][class*="active"], [class*="nav"][class*="active"]');
        activeMenus.forEach(function(element) {
            element.classList.remove('active', 'open', 'visible');
        });
        
        if (menuWasClosed) {
            console.log('[BBRU] Mobile menu closed:', reason);
        }
        
        return menuWasClosed;
    }
    
    /**
     * Force close menu with delay (prevents CSS re-opening)
     */
    function forceCloseMobileMenu(reason) {
        closeMobileMenu(reason);
        
        // Clear any existing timer
        if (forceCloseTimer) {
            clearTimeout(forceCloseTimer);
        }
        
        // Force close again after short delay to catch CSS transitions
        forceCloseTimer = setTimeout(function() {
            closeMobileMenu(reason + ' (forced)');
        }, FORCE_CLOSE_DELAY);
    }
    
    /**
     * Handle orientation change (mobile device rotation)
     */
    function handleOrientationChange() {
        const currentOrientation = window.orientation || (window.innerWidth > window.innerHeight ? 90 : 0);
        
        if (currentOrientation !== lastOrientation) {
            console.log('[BBRU] Orientation changed:', lastOrientation, '→', currentOrientation);
            forceCloseMobileMenu('orientation change');
            lastOrientation = currentOrientation;
        }
    }
    
    /**
     * Handle resize (browser window resize + orientation fallback)
     */
    function handleResize() {
        const currentWidth = window.innerWidth;
        const isMobile = currentWidth < MOBILE_BREAKPOINT;
        const isDesktop = currentWidth >= MOBILE_BREAKPOINT;
        
        // Scenario 1: Crossed from mobile to desktop breakpoint
        if (wasMobile && isDesktop) {
            console.log('[BBRU] Crossed mobile→desktop breakpoint:', MOBILE_BREAKPOINT + 'px');
            forceCloseMobileMenu('mobile→desktop breakpoint');
        }
        
        // Scenario 2: Crossed from desktop to mobile (close just in case)
        if (!wasMobile && isMobile) {
            console.log('[BBRU] Crossed desktop→mobile breakpoint:', MOBILE_BREAKPOINT + 'px');
            forceCloseMobileMenu('desktop→mobile breakpoint');
        }
        
        // Scenario 3: Width changed significantly while staying in mobile range
        if (isMobile && Math.abs(currentWidth - lastViewportWidth) > 50) {
            console.log('[BBRU] Significant mobile resize:', lastViewportWidth, '→', currentWidth);
            forceCloseMobileMenu('mobile resize');
        }
        
        // Update tracking
        lastViewportWidth = currentWidth;
        wasMobile = isMobile;
        
        // Also check orientation (for devices without orientationchange event)
        handleOrientationChange();
    }
    
    /**
     * Debounced resize handler
     */
    function onResize() {
        if (resizeTimer) {
            clearTimeout(resizeTimer);
        }
        
        resizeTimer = setTimeout(handleResize, RESIZE_DEBOUNCE);
    }
    
    /**
     * Watch for checkbox state changes (prevents menu from re-opening)
     */
    function setupCheckboxObserver() {
        const mainMenuCheckbox = document.getElementById('open-mobile-nav');
        
        if (!mainMenuCheckbox) {
            console.warn('[BBRU] Menu checkbox not found on initial load');
            return;
        }
        
        // Use MutationObserver to watch for attribute changes
        const observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'checked') {
                    // If checkbox was programmatically checked during a resize/orientation event, uncheck it
                    const currentWidth = window.innerWidth;
                    const isDesktop = currentWidth >= MOBILE_BREAKPOINT;
                    
                    if (isDesktop && mainMenuCheckbox.checked) {
                        console.log('[BBRU] Menu re-opened on desktop - force closing');
                        mainMenuCheckbox.checked = false;
                    }
                }
            });
        });
        
        observer.observe(mainMenuCheckbox, {
            attributes: true,
            attributeFilter: ['checked']
        });
        
        console.log('[BBRU] Checkbox observer initialized');
    }
    
    /**
     * Initialize on DOM ready
     */
    function init() {
        // Listen for orientation change (iOS and modern browsers)
        if ('onorientationchange' in window) {
            window.addEventListener('orientationchange', function() {
                console.log('[BBRU] orientationchange event fired');
                handleOrientationChange();
            });
        }
        
        // Listen for resize (all devices, includes orientation fallback)
        window.addEventListener('resize', onResize);
        
        // Setup mutation observer for checkbox state
        setupCheckboxObserver();
        
        // Initial state check
        const currentWidth = window.innerWidth;
        const isDesktop = currentWidth >= MOBILE_BREAKPOINT;
        
        if (isDesktop) {
            // If starting on desktop, make sure menu is closed
            closeMobileMenu('init - desktop viewport');
        }
        
        console.log('[BBRU] Orientation/resize fix v2 initialized');
        console.log('[BBRU] Current viewport:', currentWidth + 'px', isDesktop ? '(desktop)' : '(mobile)');
        console.log('[BBRU] Mobile breakpoint:', MOBILE_BREAKPOINT + 'px');
    }
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        // DOM already loaded
        init();
    }
    
    // Expose utility for debugging
    window.bbru = window.bbru || {};
    window.bbru.closeMobileMenu = function() {
        closeMobileMenu('manual');
    };
    window.bbru.orientationFixActive = true;
    
})();
